import { useEffect, useState } from "react"

class TrieTreeNode {
  /* TrieTreeNode: A node for use in a trie tree that stores a single
        character from a string and a structure of children nodes below it, which
        associates the next character in a string to the next node along its path from
        the tree's root node to a terminal node that marks the end of the string. */
  terminal: boolean
  character?: string
  children: Map<string, TrieTreeNode>

  constructor(character?: string) {
    /* Initialize this trie tree node with the given character value, an
             empty structure of children nodes, and a boolean terminal property. */
    this.character = character
    this.children = new Map<string, TrieTreeNode>()
    this.terminal = false
  }

  isTerminal(): boolean {
    /* Return True if this trie tree node terminates a string. */
    return this.terminal
  }

  hasChild(character: string): boolean {
    /* Return True if this trie tree node has a child node that
            represents the given character amongst its children. */
    return this.children.has(character)
  }

  getChild(character: string): TrieTreeNode {
    /* Return this trie tree node's child node that represents the given
               character if it is amongst its children, or raise ValueError if not. */
    if (this.hasChild(character)) {
      return this.children.get(character)!
    }
    throw new Error(`No child exists for: ${character}`)
  }

  addChild(character: string, child_node: TrieTreeNode): void {
    /* Add the given character and child node as a child of this node, or
               raise ValueError if given character is amongst this node's children. */
    if (!this.hasChild(character)) {
      this.children.set(character, child_node)
    } else {
      throw new Error(`Child exists for: ${character}`)
    }
  }
}

class TrieTree {
  /* TrieTree: A multi-way prefix tree that stores strings with efficient
        methods to insert a string into the tree, check if it contains a matching
        string, and retrieve all strings that start with a given prefix string. */
  strings?: string[]
  START_CHARACTER: string
  root: TrieTreeNode
  size: number

  constructor(strings: string[] = []) {
    /* Initialize this trie tree and insert the given strings, if any. */
    this.strings = strings
    // Constant for the start character stored in the trie tree's root node
    this.START_CHARACTER = ""
    // Create a new root node with the start characater
    this.root = new TrieTreeNode(this.START_CHARACTER)
    // Count the number of strings inserted into the tree
    this.size = 0

    // Insert each string, if any where given
    if (Array.isArray(this.strings) && this.strings.length > 0) {
      for (let i = 0; i < this.strings!.length; i += 1) {
        this.insert(strings[i])
      }
    }
  }

  private findNode(str: string): [TrieTreeNode, number] {
    /* Return a pair containing the deepest node in this trie tree that
            matches the longest prefix of the given string and the node's depth.
            The depth returned is equal to the number of prefix characters matched.
            Search is done iteratively with a loop starting from the root node. */

    // Match the empty string
    if (str.length === 0) {
      return [this.root, 0]
    }

    // Start with the root node and count depth
    let [node, depth] = [this.root, 0]

    for (let i = 0; i < str.length; i += 1) {
      if (node.hasChild(str[i])) {
        node = node.getChild(str[i]) // Found the char, go next
        depth += 1
      } else {
        // prevents showing the completions despite typing a character that's not a child
        return [node, 0]
      }
    }
    return [node, depth]
  }

  private traverse(
    node: TrieTreeNode,
    prefix: string,
    visit: (c: string) => void,
  ): void {
    /* Traverse this trie tree with recursive depth-first traversal.
            Start at the given node with the given prefix representing its path in
            this prefix tree and visit each node with the given visit function. */

    // Once the node is filled with characters and contains a terminal node, it'll append
    if (node.isTerminal()) {
      visit(prefix)
    }

    for (const char of node.children.keys()) {
      const next_node = node.getChild(char)
      this.traverse(next_node, prefix + char, visit)
    }
  }

  isEmpty(): boolean {
    /* Return True if this trie tree is empty (contains no strings). */
    return this.size === 0
  }

  contains(str: string): boolean {
    /* Return true if this prefix tree contains the given string. */
    let node = this.root
    for (let i = 0; i < str.length; i += 1) {
      if (node.hasChild(str[i])) {
        node = node.getChild(str[i])
      } else {
        return false
      }
    }
    return node.isTerminal()
  }

  insert(str: string): void {
    /* Insert the given string into this trie tree. */
    let node = this.root

    for (let i = 0; i < str.length; i += 1) {
      if (node.hasChild(str[i])) {
        node = node.getChild(str[i]) // Next node
      } else {
        node.addChild(str[i], new TrieTreeNode(str[i])) // New node
        node = node.getChild(str[i]) // Next node
      }
    }

    if (!node.isTerminal()) {
      this.size += 1
      node.terminal = true // Last node is terminal
    }
  }

  complete(prefix: string): string[] {
    /* Return a list of all strings stored in this trie tree that start
               with the given prefix string. */

    // Create a list of completions in the trie tree
    const completions: string[] = []

    // Pull out the values returned from findNode
    const [node, depth] = this.findNode(prefix)

    // No node was found
    if (depth === 0) {
      return completions
    }

    // A node was retrieved, traverse it.
    this.traverse(node, prefix, completions.push.bind(completions))
    return completions
  }

  allTreeStrings(): string[] {
    /* Return a list of all strings stored in this trie tree. */

    // Create a list of all strings in prefix tree
    const all_strings: string[] = []
    this.traverse(this.root, "", all_strings.push.bind(all_strings))
    return all_strings
  }
}

// State properties in this component
interface AppState {
  searchTree: TrieTree
  searchCorpus?: boolean
}

export function useAutocomplete(
  searchTerm: string,
  corpus: string[] = [],
): string[][] {
  // Create component state
  const [search, setSearch] = useState<AppState>({
    searchTree: new TrieTree(),
    searchCorpus: false,
  })

  // Destructure the values out of state
  const { searchTree, searchCorpus } = search

  useEffect(() => {
    // Adds corpus to the tree if added.
    if (!searchCorpus && corpus.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearch({
        searchTree: new TrieTree(corpus),
        searchCorpus: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpus])

  if (searchTerm.length > 0) {
    return [searchTree.complete(searchTerm)]
  }

  if (searchTerm.length === 0 && searchTerm === "")
    return [searchTree.allTreeStrings()]

  return [[]]
}
